import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Collapse } from '@material-ui/core';
import Client from '@spike/client-model';
import Pet, { createEmptyPet } from '@spike/pet-model';
import { PetsStatus, fetchPetsThunk, getPetThunk, reset } from '@spike/pets-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import AddPet from 'components/Pet/AddPet';
import PetRowCard from 'components/UI/PetCard';
import Spinner from 'components/UI/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import styles from './LeadPetsTab.module.scss';

interface LeadPetsTabProps {
    lead: Client;
}

type OpenPanel = { kind: 'add'; pet: Pet } | { kind: 'pet'; petId: number };

const ADD_PET_FORM_ID = 'lead-pets-add-pet-form';

const petPanelId = (petId: number): string => `lead-pets-panel-${petId}`;

const LeadPetsTab: FunctionComponent<LeadPetsTabProps> = ({ lead }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);
    const hydratedPet = useSelector<RootState, Pet | undefined>(state => state.pets.pet);

    const [isFetching, setIsFetching] = useState(false);
    const [openPanel, setOpenPanel] = useState<OpenPanel | undefined>(undefined);
    const [renderedPetPanelIds, setRenderedPetPanelIds] = useState<Array<number>>([]);

    const collapsingDraftRef = useRef<Pet | undefined>(undefined);
    const panelPetsRef = useRef<Record<number, Pet>>({});

    const leadId = lead.id;

    const openPetId = openPanel?.kind === 'pet' ? openPanel.petId : undefined;
    const isAddOpen = openPanel?.kind === 'add';
    const openPanelPet = openPetId !== undefined && hydratedPet?.id === openPetId ? hydratedPet : undefined;

    if (openPetId !== undefined && openPanelPet !== undefined) {
        panelPetsRef.current[openPetId] = openPanelPet;
    }

    if (openPanel?.kind === 'add') {
        collapsingDraftRef.current = openPanel.pet;
    }

    const draftPet = openPanel?.kind === 'add' ? openPanel.pet : collapsingDraftRef.current;

    useEffect(() => {
        if (leadId !== undefined) {
            setIsFetching(true);
            dispatch(fetchPetsThunk(apiClientWrapper, leadId));
        }

        return () => {
            dispatch(reset());
        };
    }, []);

    useEffect(() => {
        if (openPetId === undefined) {
            return;
        }

        setRenderedPetPanelIds(current => (current.includes(openPetId) ? current : [...current, openPetId]));
        dispatch(getPetThunk(apiClientWrapper, openPetId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPetId]);

    useNonInitialEffect(() => {
        if (isFetching && (petsStatus === PetsStatus.FetchSuccess || petsStatus === PetsStatus.Error)) {
            setIsFetching(false);
        }
        if (openPetId !== undefined && openPanelPet === undefined && petsStatus === PetsStatus.Error) {
            setOpenPanel(undefined);
        }
    }, [petsStatus]);

    const collapsePanel = () => {
        setOpenPanel(undefined);
    };

    const togglePetPanel = (petId: number | undefined) => {
        if (petId === undefined) {
            return;
        }

        setOpenPanel(current =>
            current?.kind === 'pet' && current.petId === petId ? undefined : { kind: 'pet', petId }
        );
    };

    const toggleAddPet = () => {
        if (openPanel?.kind === 'add') {
            collapsePanel();
            return;
        }

        if (leadId === undefined) {
            return;
        }

        setOpenPanel({ kind: 'add', pet: createEmptyPet(uuid, leadId) });
    };

    const handlePetCreated = () => {
        setOpenPanel(undefined);

        if (leadId !== undefined) {
            setIsFetching(true);
            dispatch(fetchPetsThunk(apiClientWrapper, leadId));
        }
    };

    const forgetPetPanel = (petId: number) => {
        setRenderedPetPanelIds(current => current.filter(id => id !== petId));
        delete panelPetsRef.current[petId];
    };

    const addPetHeader = (
        <button
            type="button"
            className={`${styles.addPet} ${isAddOpen ? styles.addPetExpanded : ''}`}
            onClick={toggleAddPet}
            aria-expanded={isAddOpen}
            aria-controls={isAddOpen ? ADD_PET_FORM_ID : undefined}
        >
            <FontAwesomeIcon
                key={isAddOpen ? 'collapse' : 'expand'}
                icon={isAddOpen ? faChevronUp : faPlus}
                className={styles.addPetIcon}
            />
            Add Pet
        </button>
    );

    const petRow = (pet: Pet) => {
        const petId = pet.id;

        if (petId === undefined) {
            return null;
        }

        const isOpen = openPetId === petId;
        const panelPet = panelPetsRef.current[petId];

        return (
            <div
                key={petId}
                className={styles.petRow}
            >
                <button
                    type="button"
                    className={`${styles.row} ${isOpen ? styles.rowExpanded : ''}`}
                    onClick={() => togglePetPanel(petId)}
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? petPanelId(petId) : undefined}
                >
                    <PetRowCard
                        pet={pet}
                        showWeight
                        className={styles.petCard}
                    />
                </button>
                <Collapse
                    in={isOpen}
                    timeout={320}
                    unmountOnExit
                    onExited={() => forgetPetPanel(petId)}
                >
                    {renderedPetPanelIds.includes(petId) && (
                        <div
                            id={petPanelId(petId)}
                            className={styles.formRow}
                        >
                            {panelPet === undefined ? (
                                <div className={styles.stateContainer}>
                                    <Spinner />
                                </div>
                            ) : (
                                <AddPet
                                    pet={panelPet}
                                    embedded
                                    onSaved={collapsePanel}
                                    onSelect={collapsePanel}
                                    onDeleted={collapsePanel}
                                />
                            )}
                        </div>
                    )}
                </Collapse>
            </div>
        );
    };

    return (
        <div className={styles.root}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Pets</h3>

                <div className={styles.card}>
                    {isFetching ? (
                        <div className={styles.stateContainer}>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            {pets.length === 0 ? (
                                <p className={styles.empty}>There are no pets for this lead.</p>
                            ) : (
                                pets.map(petRow)
                            )}
                            {addPetHeader}
                            <Collapse
                                in={isAddOpen}
                                timeout={320}
                                unmountOnExit
                                onExited={() => {
                                    collapsingDraftRef.current = undefined;
                                }}
                            >
                                {draftPet !== undefined && (
                                    <div
                                        id={ADD_PET_FORM_ID}
                                        className={styles.formRow}
                                    >
                                        <AddPet
                                            pet={draftPet}
                                            embedded
                                            onSaved={handlePetCreated}
                                            onSelect={collapsePanel}
                                            onDeleted={collapsePanel}
                                        />
                                    </div>
                                )}
                            </Collapse>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeadPetsTab;
